/**
 * 实时提示条 —— 架构 §7.2「用户**不在**流水页 ⇒ 顶部轻提示条」的端侧落地（批次 `66`）
 *
 * 依据：L 域 PRD **§5.2 口径 5**（增量提示）· **§7.2 `AC-L2.8`** · 架构 **§7.2** ·
 * `DR-018` · 文案见 `features/realtime-toast-labels.ts`，判定见 `features/realtime-toast.ts`。
 *
 * ## 🔴 本组件是**受控**的（零状态、零订阅）
 *
 * ```tsx
 * <RealtimeToast state={state} onView={...} onDismiss={...} />
 * ```
 *
 * 它**自己不开 socket、自己不计时、自己不管合并** —— 那些全在别处（见下）。
 * 为什么：本项目端侧测试基座是 `renderToStaticMarkup`（**不跑 effect、无 layout engine**）
 * ⇒ 任何"靠 effect 驱动的时序行为"都**证不了**（铁律 43）。受控组件把**判定与呈现分开**，
 * 于是 100% 的判定逻辑都能被 spec 用"给一个 state、断言一段输出"覆盖住。
 *
 * ## 🔴 谁负责什么（三层，各一处实现）
 *
 * | 事 | 在哪 | 为什么不在组件里 |
 * |---|---|---|
 * | **该不该弹 / 文案是什么 / 合并计数** | `features/realtime-toast.ts`（纯函数） | 组件里算数 = 端侧口径（违反纪律 ①）；且时序耦合 ⇒ 测不了 |
 * | **订阅、闸门、合并的调用点** | `api/realtime.ts` 的 `useRealtime()` | 订阅与失效刷新**必须同一个 socket**（第二处连接 = 两个"实时实现"互相踩） |
 * | **渲染** | 本文件 | —— |
 *
 * ## ⚠️ 为什么用 `c-notice`（琥珀）而不是 `c-alert`（朱红）
 *
 * 铁律 8：`--c-alert` 朱红 = **已超/错误**（如超预算），`--c-notice` 琥珀 = **告知**。
 * "别人改了 1 笔"是**中性告知**，不是错误 —— 用朱红会让用户以为出事了。
 * 🔴 同时**不用品牌绿**：绿色在中国约定里表"负/支出"（铁律 8），拿它当提示底色会与语义撞车。
 *
 * ## ⚠️ 放在顶栏之下、内容之上（不是 fixed 浮层）
 *
 * 原型/架构写的是「**顶部**轻提示条」。做成 `sticky`（跟随滚动）而不 `fixed`：
 * ① 不遮挡内容（提示条有高度，`fixed` 会压在正文上）；② 与本仓 `Topbar` 的 `position: sticky`
 * 同一手法（`Shell.tsx`），不是新范式。
 */
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { ToastState } from '../features/realtime-toast.js';
import { TOAST_ARIA, TOAST_VIEW, TOAST_VIEW_HINT } from '../features/realtime-toast-labels.js';

/** 受控 props —— 🔴 `state === null` ⇒ 不渲染任何东西（没有"空提示条"这种中间态） */
export interface RealtimeToastProps {
  /** 当前该显示的提示（`null` = 不显示）—— 由 `features/realtime-toast.ts` 算好 */
  readonly state: ToastState | null;
  /** 点「查看」—— 通常 = 失效当前页查询并跳到流水页（调用方决定，本组件不导航） */
  readonly onView: () => void;
  /** 关闭 —— 调用方把 state 置 `null` */
  readonly onDismiss: () => void;
}

export function RealtimeToast({ state, onView, onDismiss }: RealtimeToastProps): ReactNode {
  const tk = useTheme().jiaying.tokens;
  if (state === null) return null;
  return (
    <Box
      // 🔴 `role="status"` + `aria-live="polite"`：读屏会念出「协作动态」（不抢焦点）。
      //    用 `status` 而不是 `alert` —— `alert` 会**打断**用户当前操作，提示条不该那么霸道。
      role="status"
      aria-live="polite"
      aria-label={TOAST_ARIA}
      data-testid="realtime-toast"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        position: 'sticky',
        top: tk['topbar-h'],
        zIndex: 30,
        px: tk['pad-card'],
        py: '10px',
        bgcolor: tk['c-notice-bg'],
        borderBottom: '1px solid',
        borderColor: tk['c-notice'],
        color: tk['c-text'],
      }}
    >
      <Typography
        variant="body2"
        data-testid="realtime-toast-text"
        sx={{ fontSize: tk['fs-13'], fontWeight: 600, minWidth: 0, flex: 1 }}
      >
        {state.text}
      </Typography>
      {/* 🔴 「查看」的 `aria-describedby` 指向隐藏说明 —— 只写"查看"两个字，读屏不知道查看什么 */}
      <Typography
        id="realtime-toast-hint"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {TOAST_VIEW_HINT}
      </Typography>
      <Button
        size="small"
        data-testid="realtime-toast-view"
        aria-describedby="realtime-toast-hint"
        onClick={onView}
        sx={{ color: tk['c-notice'], fontWeight: 700, whiteSpace: 'nowrap', minWidth: 0 }}
      >
        {TOAST_VIEW}
      </Button>
      <Button
        size="small"
        data-testid="realtime-toast-dismiss"
        onClick={onDismiss}
        sx={{ color: tk['c-text-2'], whiteSpace: 'nowrap', minWidth: 0 }}
      >
        忽略
      </Button>
    </Box>
  );
}
